import { useEffect, useMemo, useRef, useState } from 'react'
import { DndProvider, useDrag, useDrop } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import LoadingIcon from 'components/atoms/LoadingIcon'
import { Formik, Form, Field, FieldArray, setNestedObjectValues } from 'formik'
import * as Yup from 'yup'
import { CameraIcon } from '@heroicons/react/solid'
import { CheckIcon, DownloadIcon, MenuAlt4Icon, PlusCircleIcon, RefreshIcon, TrashIcon, TruckIcon, XIcon } from '@heroicons/react/outline'
import { size, get, xor, isArray, isObject, padStart, split, trim, some, every, find, findIndex, isNil, isString, toLower, forEach, assign } from 'lodash'
import Input from 'components/atoms/Input'
import notify from 'components/atoms/Notify'
import Modal from 'components/molecules/Modal'
import Selection from 'components/molecules/Selection'
import apiPost from 'lib/network/apiPost'
import apiPut from 'lib/network/apiPut'
import apiDelete from 'lib/network/apiDelete'
import { getLocalizedCountries } from './modals/countries'
import TextArea from 'antd/lib/input/TextArea'
import classNames from 'classnames'
import Switch from 'components/atoms/Switch'
import apiGet from 'lib/network/apiGet'
import CompanyEcommerceTab from 'pages/company/CompanyEcommerceTab'

const STEP_ONE_FIELDS = ['logoUrl', 'name', 'country', 'governorate', 'email', 'address', 'minAge', 'maxAge']
const STEP_FOUR_FIELDS = ['vatTax', 'paymentGatewayPercentage']
const REQUIRED_FIELDS = new Set([...STEP_ONE_FIELDS, ...STEP_FOUR_FIELDS, 'branch', 'phones.title', 'phones.phone'])
const LabelContent = ({ label, required }) => (
  <span className="inline-flex items-center gap-1">
    <span>{label}</span>
    {required && <span className="text-red-500">*</span>}
  </span>
)

/** Section card used on Configurations / Delivery tabs */
const ConfigSection = ({ title, description, children }) => (
  <section className="flex h-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
    <div className="border-b border-gray-100 bg-gray-50 px-4 py-3 sm:px-5">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      {description ? <p className="mt-0.5 text-xs text-gray-500">{description}</p> : null}
    </div>
    <div className="flex-1 divide-y divide-gray-100">{children}</div>
  </section>
)

/** Toggle row; optional children render under the switch (e.g. related number field) */
const ConfigToggleRow = ({ id, label, value, onChange, disabled, hint, children }) => (
  <div className="px-4 py-4 sm:px-5">
    <div className="flex items-center justify-between gap-4">
      <div className="min-w-0">
        <label htmlFor={id} className="text-sm font-medium text-gray-700">
          {label}
        </label>
        {hint ? <p className="mt-1 text-xs text-gray-500">{hint}</p> : null}
      </div>
      <Switch id={id} value={value} disabled={disabled} onChange={onChange} />
    </div>
    {children ? <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-3 sm:p-4">{children}</div> : null}
  </div>
)

/** Label + input row inside a config section */
const ConfigFieldRow = ({ htmlFor, label, required, children }) => (
  <div className="px-4 py-4 sm:px-5">
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-gray-700">
      <LabelContent label={label} required={required} />
    </label>
    <div className="w-full">{children}</div>
  </div>
)

const isFieldRequired = fieldName => REQUIRED_FIELDS.has(fieldName)
const getTrimmedValue = value => (isString(value) ? trim(value) : value)

const FEATURED_CAR_DND = 'featuredCarOrder'

const getCarThumb = car => car?.carImages?.[0]?.url || car?.carImages?.[0]?.thumbnailUrl || ''
const getCarTitle = (car, isArabic) => {
  const makeName = isArabic ? car?.car?.make?.makeAr || car?.car?.make?.make : car?.car?.make?.make
  const modelName = isArabic ? car?.car?.model?.modalAr || car?.car?.model?.modal : car?.car?.model?.modal
  return trim(`${makeName || ''} ${modelName || ''} ${car?.modelYear || ''}`) || car?.registrationNumber || 'Car'
}

// Shared visual body for a featured-car card (used by both the selectable grid and the ordered list).
const FeaturedCarCardBody = ({ car, isArabic }) => {
  const thumb = getCarThumb(car)
  const title = getCarTitle(car, isArabic)

  return (
    <>
      <div className="relative h-24 w-full bg-slate-100">
        {thumb ? (
          <img className="h-full w-full object-cover" src={thumb} alt="" />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200/90">
            <TruckIcon className="h-9 w-9 text-slate-400" strokeWidth={1.25} />
          </div>
        )}
      </div>
      <div className="p-2.5">
        <h4 className="truncate text-sm font-bold leading-snug text-slate-900" title={title}>
          {title}
        </h4>
        {car?.registrationNumber && <p className="mt-0.5 truncate font-mono text-[11px] text-slate-500">{car.registrationNumber}</p>}
      </div>
    </>
  )
}

// A draggable card representing a selected featured car, in display order.
const DraggableFeaturedCarCard = ({ car, index, isArabic, moveCar, onRemove }) => {
  const ref = useRef(null)

  const [{ isDragging }, drag] = useDrag({
    type: FEATURED_CAR_DND,
    item: { index },
    collect: monitor => ({ isDragging: monitor.isDragging() }),
  })

  const [, drop] = useDrop({
    accept: FEATURED_CAR_DND,
    hover: item => {
      if (!ref.current) return
      const dragIndex = item.index
      const hoverIndex = index
      if (dragIndex === hoverIndex) return
      moveCar(dragIndex, hoverIndex)
      item.index = hoverIndex
    },
  })

  drag(drop(ref))

  return (
    <div
      ref={ref}
      className={classNames(
        'group relative flex cursor-move flex-col overflow-hidden rounded-xl border bg-white shadow-sm transition-all',
        isDragging ? 'opacity-50 shadow-lg ring-2 ring-pink-300' : 'border-slate-200/70 hover:border-slate-300 hover:shadow-md'
      )}
    >
      <span className="absolute left-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-dark-600 text-[11px] font-bold text-white shadow">{index + 1}</span>
      <button
        type="button"
        onClick={onRemove}
        className="absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-slate-500 shadow ring-1 ring-slate-200 transition-colors hover:bg-rose-500 hover:text-white"
        title="Remove"
      >
        <XIcon className="h-3.5 w-3.5" />
      </button>
      <MenuAlt4Icon className="absolute bottom-2 right-2 z-10 h-4 w-4 text-slate-300" />
      <FeaturedCarCardBody car={car} isArabic={isArabic} />
    </div>
  )
}

// A selectable car card in the "all cars" grid.
const SelectableFeaturedCarCard = ({ car, isArabic, isSelected, order, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className={classNames(
      'group relative flex flex-col overflow-hidden rounded-xl border bg-white text-left shadow-sm transition-all',
      isSelected ? 'border-dark-600 ring-2 ring-dark-600/30' : 'border-slate-200/70 hover:border-slate-300 hover:shadow-md'
    )}
  >
    <span
      className={classNames(
        'absolute right-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold shadow transition-colors',
        isSelected ? 'bg-dark-600 text-white' : 'bg-white/90 text-transparent ring-1 ring-slate-300 group-hover:text-slate-300'
      )}
    >
      {isSelected ? order : <CheckIcon className="h-3.5 w-3.5" />}
    </span>
    <FeaturedCarCardBody car={car} isArabic={isArabic} />
  </button>
)

const normalizeToIdArray = value => {
  if (!value) return []
  if (isArray(value)) {
    return value.map(item => (isObject(item) && !isArray(item) && item?._id != null ? String(item._id) : String(item))).filter(Boolean)
  }
  if (isString(value)) {
    return split(value, ',')
      .map(item => trim(item))
      .filter(Boolean)
  }
  return []
}
const numberTransform = value => {
  if (value === '' || value === null || value === undefined) return undefined
  return value
}

const toBoolean = value => value === true || value === 'true' || value === 1 || value === '1'

const flattenFormikErrors = (errors, prefix = '') => {
  const result = {}
  if (!isObject(errors)) return result

  forEach(errors, (value, key) => {
    const path = prefix ? `${prefix}.${key}` : key
    if (isString(value)) {
      result[path] = value
    } else if (isArray(value)) {
      forEach(value, (item, index) => {
        if (!item) return
        if (isString(item)) {
          result[`${path}[${index}]`] = item
        } else if (isObject(item)) {
          assign(result, flattenFormikErrors(item, `${path}[${index}]`))
        }
      })
    } else if (isObject(value)) {
      assign(result, flattenFormikErrors(value, path))
    }
  })
  return result
}

const deliveryFeeWaiverMinDaysSchema = Yup.mixed().when('deliveryFeeWaiverEnabled', {
  is: toBoolean,
  then: () => Yup.number().transform(numberTransform).typeError('Min booking days must be a number').required('Min booking days is required'),
  otherwise: () => Yup.mixed().notRequired(),
})

// Only enforced while the office has the minimum switched on.
const minRentalDaysSchema = Yup.mixed().when('minRentalDaysEnabled', {
  is: toBoolean,
  then: () =>
    Yup.number()
      .transform(numberTransform)
      .typeError('Minimum rental days must be a number')
      .integer('Minimum rental days must be a whole number')
      .min(1, 'Minimum rental days must be at least 1')
      .required('Minimum rental days is required'),
  otherwise: () => Yup.mixed().notRequired(),
})

const baseValidationShape = {
  name: Yup.string().transform(getTrimmedValue).required('Company name is required'),
  country: Yup.string().transform(getTrimmedValue).required('Country is required'),
  governorate: Yup.string().transform(getTrimmedValue).required('City is required'),
  email: Yup.string()
    .transform(getTrimmedValue)
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, 'Enter a valid email address')
    .required('Email is required'),
  address: Yup.string().transform(getTrimmedValue).required('Address is required'),
  minAge: Yup.number().typeError('Minimum age must be a number').required('Minimum age is required'),
  maxAge: Yup.number()
    .typeError('Maximum age must be a number')
    .required('Maximum age is required')
    .when('minAge', (minAge, schema) => (!isNil(minAge) ? schema.min(minAge, 'Maximum age must be greater than or equal to minimum age') : schema)),
  vatTax: Yup.number().typeError('VAT tax must be a number').required('VAT tax is required'),
  paymentGatewayPercentage: Yup.number()
    .typeError('Payment gateway percentage must be a number')
    .min(0, 'Payment gateway percentage must be at least 0')
    .max(100, 'Payment gateway percentage cannot be more than 100')
    .required('Payment gateway percentage is required'),
  // nationalDayDiscount: Yup.number()
  //   .transform(numberTransform)
  //   .typeError('National day discount must be a number')
  //   .when('nationalDayOffer', (nationalDayOffer, schema) => (nationalDayOffer ? schema.required('National day discount is required') : schema.notRequired())),
  deliveryFeeWaiverMinDays: deliveryFeeWaiverMinDaysSchema,
  minRentalDays: minRentalDaysSchema,
  phones: Yup.array().of(
    Yup.object({
      title: Yup.string().transform(getTrimmedValue).required('Phone title is required'),
      phone: Yup.string()
        .transform(getTrimmedValue)
        .matches(/^\+?[0-9]+$/, 'Invalid phone number')
        .required('Phone number is required'),
    })
  ),
}

const getValidationSchema = isEdit =>
  Yup.object({
    logoUrl: isEdit ? Yup.string().nullable() : Yup.string().required('Company logo is required'),
    ...baseValidationShape,
  })

const getEditValidationSchema = (totalCompanyCars = 0, companyCarsLoaded = false) =>
  Yup.object({
    logoUrl: Yup.string().nullable().notRequired(),
    name: Yup.string().transform(getTrimmedValue).required('Name is not allowed to be empty'),
    vatTax: Yup.number().transform(numberTransform).typeError('VatTax must be a number').required('VatTax is not allowed to be empty'),
    paymentGatewayPercentage: Yup.number()
      .transform(numberTransform)
      .typeError('PaymentGatewayPercentage must be a number')
      .min(0, 'Payment gateway percentage must be at least 0')
      .max(100, 'Payment gateway percentage cannot be more than 100')
      .required('PaymentGatewayPercentage is not allowed to be empty'),
    branch: Yup.mixed().test('branch-required', 'Branch is not allowed to be empty', value => {
      if (isArray(value)) return value.length > 0
      return Boolean(value)
    }),
    // deliveryFeeWaiverMinDays: Yup.number()
    //   .transform(numberTransform)
    //   .typeError('Min booking days must be a number')
    //   .when('deliveryFeeWaiverEnabled', (deliveryFeeWaiverEnabled, schema) => (deliveryFeeWaiverEnabled ? schema.required('Min booking days is required') : schema.notRequired())),
    featuredCarsPerRow: Yup.number()
      .transform(numberTransform)
      .typeError('Cars per row must be a number')
      .min(0, 'Cars per row cannot be negative')
      .test('within-total-cars', `Cars per row must be less than or equal to ${totalCompanyCars}`, value => {
        if (!value || value <= 0) return true
        if (!companyCarsLoaded) return true
        return value <= totalCompanyCars
      })
      .test('featured-cars-required', 'Select at least one featured car when cars per row is greater than 0', function (value) {
        if (!value || value <= 0) return true
        if (!companyCarsLoaded) return true
        const featuredCars = this.parent.featuredCars
        return isArray(featuredCars) && featuredCars.length > 0
      })
      .notRequired(),
    featuredCars: Yup.array().notRequired(),
    deliveryFeeWaiverMinDays: deliveryFeeWaiverMinDaysSchema,
    minRentalDays: minRentalDaysSchema,
  })

const CompanyProfile = ({ readOnly, loading, refetch, companyData, isEdit, id, roles }) => {
  const { t, i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const localizedCountries = useMemo(() => getLocalizedCountries(i18n.language), [i18n.language])
  const [previews, setPreviews] = useState({})
  const [files, setFiles] = useState({})
  const [profile, setProfile] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [params, setParams] = useState({ t: 1 })
  const [branchNames, setBranchNames] = useState([])
  const [isFetchingBranches, setIsFetchingBranches] = useState(true)
  const [masterCountries, setMasterCountries] = useState([])
  const [masterCities, setMasterCities] = useState([])
  const [userOptions, setUserOptions] = useState([])
  const [campaignOptions, setCampaignOptions] = useState([])
  const [activeStep, setActiveStep] = useState(1)
  const [dnsBannerRefreshing, setDnsBannerRefreshing] = useState(false)
  const [companyCars, setCompanyCars] = useState([])
  const [isFetchingCompanyCars, setIsFetchingCompanyCars] = useState(false)
  const [companyCarsLoaded, setCompanyCarsLoaded] = useState(false)
  const [showCarsModal, setShowCarsModal] = useState(false)
  const [featuredCarsBackup, setFeaturedCarsBackup] = useState([])
  const [featuredCarsPreSelectAll, setFeaturedCarsPreSelectAll] = useState(null)

  const history = useHistory()
  const profileRef = useRef(null)
  const user = useSelector(state => get(state, 'user.user'))

  const isPlatformAdmin = Boolean(some(user?.roles, { type: 'admin' }))
  const canBranding = isPlatformAdmin || user?.canManageBranding
  const canDns = isPlatformAdmin || user?.canManageDns
  const canEcommerce = canBranding || canDns

  const isDnsProvisioningLocked = useMemo(() => companyData?.dnsStatus === 'pending' || companyData?.dnsStatus === 'provisioning', [companyData?.dnsStatus])

  const handleDnsStatusRefresh = async () => {
    if (!refetch) return
    setDnsBannerRefreshing(true)
    try {
      await refetch()
    } finally {
      setDnsBannerRefreshing(false)
    }
  }

  const tabs = [
    { id: 1, name: t('companyProfile.tabs.companyDetail'), href: '#', current: false },
    { id: 2, name: t('companyProfile.tabs.officeInstructions'), href: '#', current: false },
    { id: 3, name: t('companyProfile.tabs.companyPhones'), href: '#', current: false },
    { id: 4, name: t('companyProfile.tabs.configurations'), href: '#', current: false },
    ...(isEdit ? [{ id: 5, name: 'Delivery Configurations', href: '#', current: false }] : []),
    ...(isEdit ? [{ id: 6, name: 'Featured Settings', href: '#', current: false }] : []),
    { id: 7, name: t('companyProfile.tabs.certificates'), href: '#', current: false },
    ...(isEdit && canEcommerce ? [{ id: 8, name: t('companyProfile.tabs.ecommerce'), href: '#', current: false }] : []),
  ]

  const getSteps = () => {
    return [
      { id: 1, name: t('companyProfile.tabs.companyDetail') },
      { id: 2, name: t('companyProfile.tabs.officeInstructions') },
      { id: 3, name: t('companyProfile.tabs.companyPhones') },
      { id: 4, name: t('companyProfile.tabs.configurations') },
      { id: 5, name: t('companyProfile.tabs.certificates') },
    ]
  }

  const Stepper = ({ steps, activeStep }) => {
    return (
      <div className="w-full">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step.id} className="flex flex-col items-center relative w-full">
              {index < steps.length - 1 && (
                <div className={`absolute top-4 left-[calc(50%+1rem)] w-[calc(100%-2rem)] h-0.5 ${activeStep > step.id ? 'bg-dark-600' : 'bg-gray-300'}`} />
              )}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                  activeStep > step.id ? 'bg-dark-600 text-white' : activeStep === step.id ? 'border-2 border-dark-600 text-dark-600' : 'border-2 border-gray-300 text-gray-400'
                }`}
              >
                {activeStep > step.id ? <CheckIcon className="w-5 h-5" /> : step.id}
              </div>
              <span className={`mt-2 text-sm ${activeStep >= step.id ? 'text-dark-600 font-medium' : 'text-gray-500'}`}>{step.name}</span>
            </div>
          ))}
        </div>
      </div>
    )
  }

  const handleNext = () => {
    if (activeStep < getSteps().length) {
      setActiveStep(activeStep + 1)
    }
  }

  const handlePrevious = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1)
    }
  }

  useEffect(() => {
    setPreviews({
      profile: companyData?.companyProfile?.profile,
      logoUrl: companyData?.logoUrl,
      uploadCertificate: companyData?.uploadCertificate,
      officeInstructionUserContract: companyData?.officeInstructionUserContract,
      cover: companyData?.companyProfile?.cover,
    })
    setProfile({
      username: companyData?.companyProfile?.username,
      ...companyData?.companyProfile?.userKyc,
      twoFactorAuth: companyData?.companyProfile?.twoFactorAuth,
    })
    fetchBranchies()
  }, [companyData])

  const fetchBranchies = async (page = 1) => {
    setIsFetchingBranches(true)
    try {
      const params = {}
      params.pageNumber = page
      params.pageSize = 1000
      const response = await apiGet('branches', params)

      if (response?.data) {
        const branches = response.data.branch.map(branch => ({
          id: branch._id,
          name: branch.name,
        }))
        setBranchNames(branches)
      } else {
        setBranchNames([])
      }
    } catch (error) {
      setBranchNames([])
    } finally {
      setIsFetchingBranches(false)
    }
  }

  const fetchUsers = async searchQuery => {
    try {
      const response = await apiGet('users', { query: searchQuery, pageSize: 200 })

      if (response?.data) {
        const users = response.data.user.map(user => ({
          id: user._id,
          name: user.fullName || user.email || user.phone,
        }))
        setUserOptions(users)
      } else {
        setUserOptions([])
      }
    } catch (error) {
      // Error fetching users setUserOptions([]) } }
      setUserOptions([])
    }
  }
  const fetchCampaigns = async () => {
    try {
      const response = await apiGet('unique-campaigns')

      if (response?.data?.campaigns) {
        const campaigns = response.data.campaigns.map(campaign => ({
          id: campaign,
          name: campaign,
        }))
        setCampaignOptions(campaigns)
      } else {
        setCampaignOptions([])
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error)
      setCampaignOptions([])
    }
  }

  useEffect(() => {
    fetchCampaigns()
    fetchCampaigns()
  }, [])

  useEffect(() => {
    fetchMasterCountries()
  }, [])

  useEffect(() => {
    const countryId = resolveCountryValue(companyData?.country)
    if (countryId) {
      fetchMasterCities(countryId)
    }
  }, [companyData?.country, masterCountries])

  useEffect(() => {
    fetchUsers('')
  }, [])

  const fetchMasterCountries = async () => {
    try {
      const response = await apiGet('countries', { pageSize: 500, pageNumber: 1 })
      if (response?.data?.countries) {
        setMasterCountries(response.data.countries)
      } else {
        setMasterCountries([])
      }
    } catch (error) {
      console.error('Error fetching countries:', error)
      setMasterCountries([])
    }
  }

  const fetchMasterCities = async countryId => {
    if (!countryId) {
      setMasterCities([])
      return
    }
    try {
      const response = await apiGet('cities', { pageSize: 500, pageNumber: 1, country: countryId })
      if (response?.data?.cities) {
        setMasterCities(response.data.cities)
      } else {
        setMasterCities([])
      }
    } catch (error) {
      console.error('Error fetching cities:', error)
      setMasterCities([])
    }
  }

  const getCountryOptions = () =>
    masterCountries
      .filter(c => c.active !== false)
      .map(c => ({
        id: c._id,
        name: isArabic && c.countryAr ? c.countryAr : c.country,
      }))

  const getGovernorateOptions = currentGovernorate => {
    const options = masterCities
      .filter(c => c.active !== false)
      .map(c => ({
        id: c.city,
        name: isArabic && c.cityAr ? c.cityAr : c.city,
      }))

    if (currentGovernorate) {
      const normalized = toLower(trim(String(currentGovernorate)))
      const hasMatch = some(options, o => toLower(o.id) === normalized)
      if (!hasMatch) {
        options.unshift({ id: currentGovernorate, name: currentGovernorate })
      }
    }

    return options
  }

  const resolveCountryValue = country => {
    if (!country) return ''
    if (isObject(country)) return country._id || ''
    const value = trim(String(country))
    if (/^[0-9a-fA-F]{24}$/.test(value)) return value
    const normalized = toLower(value)
    const match = find(masterCountries, c => toLower(c.country) === normalized)
    return match?._id || ''
  }

  const resolveGovernorateValue = governorate => {
    if (!governorate) return ''
    const normalized = toLower(trim(String(governorate)))
    const match = find(masterCities, c => toLower(c.city) === normalized)
    return match ? match.city : governorate
  }

  const fetchCompanyCars = async () => {
    if (!id) return
    setIsFetchingCompanyCars(true)
    try {
      const res = await apiGet('cars', { companyId: id, pageNumber: 1, pageSize: 1000 })
      setCompanyCars(res?.data?.rentalCars || [])
    } catch (err) {
      setCompanyCars([])
    } finally {
      setIsFetchingCompanyCars(false)
      setCompanyCarsLoaded(true)
    }
  }

  useEffect(() => {
    if (isEdit && id) {
      fetchCompanyCars()
    }
  }, [isEdit, id])

  const setCurrentTab = currentTab => setParams({ t: parseInt(currentTab, 10) })

  const addUpdateCompany = async (values, { setSubmitting }) => {
    const sanitizedPhones = isArray(values.phones)
      ? values.phones.map(phone => ({
          ...phone,
          title: getTrimmedValue(phone?.title),
          phone: getTrimmedValue(phone?.phone),
        }))
      : []

    const data = {
      name: values.name,
      country: values.country,
      governorate: values.governorate,
      email: values.email,
      address: values.address,
      maxAge: values.maxAge,
      minAge: values.minAge,
      blockedCountries: values.blockedCountries,
      branch: values.branch,
      ajarleePercentage: values.ajarleePercentage,
      hasFraudUserAccess: values.hasFraudUserAccess,
      instantBooking: values.instantBooking,
      requiresCardHandover: Boolean(values.requiresCardHandover),
      isFreeReplacementAvailable: Boolean(values.isFreeReplacementAvailable),
      isFreeBreakdownServiceAvailable: Boolean(values.isFreeBreakdownServiceAvailable),
      isExtrasEnabled: Boolean(values.isExtrasEnabled),
      includeStandardInsuranceInBookingPayment: Boolean(values.includeStandardInsuranceInBookingPayment),
      includeFullInsuranceInBookingPayment: Boolean(values.includeFullInsuranceInBookingPayment),
      brandName: values.brandName,
      certificateNumber: values.certificateNumber,
      // depositAmount: values.depositAmount,
      vatTax: values.vatTax,
      paymentGatewayPercentage: values.paymentGatewayPercentage,
      // offerEidDiscount: values?.offerEidDiscount,
      deliveryFee: values?.deliveryFee,
      isFreeDeliveryAvailable: values?.isFreeDeliveryAvailable,
      freeDeliveryFor: values?.freeDeliveryFor,
      freeDeliveryUsers: values?.freeDeliveryUsers,
      freeDeliveryCampaignUsers: values?.freeDeliveryCampaignUsers,
      effectiveStartDate: values?.effectiveStartDate,
      effectiveEndDate: values?.effectiveEndDate,
      featured: values?.featured,
      featuredEffectiveStartDate: values?.featuredEffectiveStartDate,
      featuredEffectiveEndDate: values?.featuredEffectiveEndDate,
      ...(isEdit
        ? {
            featuredCarsPerRow: values?.featuredCarsPerRow,
            featuredCars: values?.featuredCars,
          }
        : {}),
      isActive: values?.isActive,
      hideOnTenant: values?.hideOnTenant,
      showOfficeLogo: values?.showOfficeLogo,
      // nationalDayOffer: values?.nationalDayOffer,
      deliveryFeeWaiverEnabled: values?.deliveryFeeWaiverEnabled,
      // nationalDayDiscount: values?.nationalDayDiscount,
      ...(toBoolean(values?.deliveryFeeWaiverEnabled) ? { deliveryFeeWaiverMinDays: values?.deliveryFeeWaiverMinDays } : {}),
      minRentalDaysEnabled: values?.minRentalDaysEnabled,
      // Sent even while disabled so the stored value survives a toggle off/on round trip.
      minRentalDays: isNil(values?.minRentalDays) ? '' : values?.minRentalDays,
    }

    delete data.id
    delete data.logoUrl

    setIsSubmitting(true)
    const formData = new FormData()

    if (files.cover) formData.append('cover', files.cover)
    if (files.profile) formData.append(`profile`, files.profile)
    if (files.logoUrl) formData.append(`logoUrl`, files.logoUrl)
    if (files.uploadCertificate) formData.append(`uploadCertificate`, files.uploadCertificate)
    if (files.officeInstructionUserContract) formData.append(`officeInstructionUserContract`, files.officeInstructionUserContract)
    // eslint-disable-next-line

    if (isArray(values.officeInstructions)) {
      // eslint-disable-next-line
      values.officeInstructions.forEach((instruction, index) => {
        formData.append(`officeInstructions[${index}]`, JSON.stringify(instruction))
      })
    }
    if (isArray(sanitizedPhones)) {
      // eslint-disable-next-line
      sanitizedPhones.forEach((phone, index) => {
        formData.append(`phones[${index}]`, JSON.stringify(phone))
      })
    }

    // eslint-disable-next-line
    Object.keys(data).forEach(key => {
      if (data[key] === 'null' || data[key] === undefined || data[key] === null) delete data[key]
      // eslint-disable-next-line
      else if (isArray(data[key])) {
        formData.append(key, data[key].join(','))
        // eslint-disable-next-line
      } else if (isObject(data[key])) {
        formData.append(key, JSON.stringify(data[key]))
      } else {
        formData.append(key, data[key])
      }
    })

    // eslint-disable-next-line
    Object.keys(profile).forEach(key => {
      if (profile[key] === 'null' || profile[key] === undefined || profile[key] === null) delete profile[key]
      // eslint-disable-next-line
      if (typeof profile[key] === 'string' || typeof profile[key] === 'number' || typeof profile[key] === 'boolean' || Array.isArray(profile[key])) {
        formData.append(key, profile[key])
      }
    })

    try {
      if (isEdit) {
        const response = await apiPut(`company/edit/${id}`, formData)
        if (response.success) {
          notify.success(t('companyProfile.messages.companyUpdatedSuccessfully'))
          history.push('/companies')
        }
      } else {
        const response = await apiPost('add/company', formData)
        if (response.success) {
          notify.success(t('companyProfile.messages.companyAddedSuccessfully'))
          history.push('/companies')
        }
      }
    } catch (error) {
      notify.error(t('companyProfile.messages.failedToSaveCompany'))
    } finally {
      setIsSubmitting(false)
      setSubmitting(false)
    }
  }

  const deleteCompany = () => {
    notify.deleteConfirm({
      title: t('companyProfile.messages.deleteCompanyTitle'),
      message: t('companyProfile.messages.deleteCompanyMessage'),
      onConfirm: async () => {
        const response = await apiDelete(`company/delete/${id}`)
        if (response?.success) {
          history.push('/companies')
          notify.success(t('companyProfile.messages.companyDeletedSuccessfully'))
        }
      },
    })
  }

  const showPreview = (key, file) => {
    const preview = URL.createObjectURL(file)
    setPreviews({ ...previews, [key]: preview })
  }

  const formatDateForInput = isoDate => {
    if (!isoDate) return ''
    const date = new Date(isoDate)
    if (isNaN(date.getTime())) return ''

    const year = date.getFullYear()
    const month = padStart(String(date.getMonth() + 1), 2, '0')
    const day = padStart(String(date.getDate()), 2, '0')
    const hours = padStart(String(date.getHours()), 2, '0')
    const minutes = padStart(String(date.getMinutes()), 2, '0')

    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  const initialValues = isEdit
    ? {
        officeInstructions: [{ ar: '', en: '' }],
        phones: [{ title: '', phone: '' }],
        ...companyData,
        blockedCountries: normalizeToIdArray(companyData?.blockedCountries),
        branch: normalizeToIdArray(companyData?.branch),
        country: resolveCountryValue(companyData?.country || ''),
        governorate: resolveGovernorateValue(companyData?.governorate || ''),
        isFreeDeliveryAvailable: companyData?.isFreeDeliveryAvailable || false,
        deliveryFee: companyData?.deliveryFee || '',
        freeDeliveryFor: companyData?.freeDeliveryFor || '',
        freeDeliveryUsers: companyData?.freeDeliveryUsers || [],
        freeDeliveryCampaignUsers: companyData?.freeDeliveryCampaignUsers || [],
        effectiveStartDate: formatDateForInput(companyData?.effectiveStartDate || ''),
        effectiveEndDate: formatDateForInput(companyData?.effectiveEndDate || ''),
        featured: companyData?.featured || false,
        featuredEffectiveStartDate: formatDateForInput(companyData?.featuredEffectiveStartDate || ''),
        featuredEffectiveEndDate: formatDateForInput(companyData?.featuredEffectiveEndDate || ''),
        featuredCarsPerRow: companyData?.featuredCarsPerRow ?? 0,
        featuredCars: normalizeToIdArray(companyData?.featuredCars),
        isActive: companyData?.isActive ?? true,
        hideOnTenant: companyData?.hideOnTenant ?? false,
        showOfficeLogo: companyData?.showOfficeLogo ?? true,
        hasFraudUserAccess: Boolean(companyData?.hasFraudUserAccess),
        instantBooking: Boolean(companyData?.instantBooking),
        requiresCardHandover: Boolean(companyData?.requiresCardHandover),
        isFreeReplacementAvailable: Boolean(companyData?.isFreeReplacementAvailable),
        isFreeBreakdownServiceAvailable: Boolean(companyData?.isFreeBreakdownServiceAvailable),
        isExtrasEnabled: Boolean(companyData?.isExtrasEnabled),
        includeStandardInsuranceInBookingPayment: Boolean(companyData?.includeStandardInsuranceInBookingPayment),
        includeFullInsuranceInBookingPayment: Boolean(companyData?.includeFullInsuranceInBookingPayment),
        paymentGatewayPercentage: companyData?.paymentGatewayPercentage ?? '',
        // nationalDayOffer: companyData?.nationalDayOffer || false,
        deliveryFeeWaiverEnabled: toBoolean(companyData?.deliveryFeeWaiverEnabled),
        minRentalDaysEnabled: toBoolean(companyData?.minRentalDaysEnabled),
        minRentalDays: isNil(companyData?.minRentalDays) ? '' : companyData?.minRentalDays,
      }
    : {
        logoUrl: '',
        name: '',
        country: '',
        governorate: '',
        maxAge: '',
        minAge: '',
        email: '',
        address: '',
        blockedCountries: [],
        branch: [],
        ajarleePercentage: '',
        hasFraudUserAccess: false,
        instantBooking: false,
        requiresCardHandover: false,
        isFreeReplacementAvailable: false,
        isFreeBreakdownServiceAvailable: false,
        isExtrasEnabled: false,
        includeStandardInsuranceInBookingPayment: false,
        includeFullInsuranceInBookingPayment: false,
        phones: [{ title: '', phone: '' }],
        brandName: '',
        certificateNumber: '',
        // depositAmount: '',
        vatTax: '',
        paymentGatewayPercentage: '',
        deliveryFee: '',
        isFreeDeliveryAvailable: false,
        freeDeliveryFor: '',
        freeDeliveryUsers: [],
        freeDeliveryCampaignUsers: [],
        effectiveStartDate: '',
        effectiveEndDate: '',
        featured: false,
        featuredEffectiveStartDate: '',
        featuredEffectiveEndDate: '',
        isActive: true,
        hideOnTenant: false,
        showOfficeLogo: true,
        // nationalDayOffer: false,
        deliveryFeeWaiverEnabled: false,
        // nationalDayDiscount: '',
        deliveryFeeWaiverMinDays: '',
        minRentalDaysEnabled: false,
        minRentalDays: '',
        // offerEidDiscount: false,
        officeInstructions: [
          {
            title: { ar: '', en: '' },
            description: { ar: '', en: '' },
          },
        ],
      }

  const formikValidationSchema = isEdit ? getEditValidationSchema(companyCars.length, companyCarsLoaded) : getValidationSchema(false)

  return (
    <Formik initialValues={initialValues} onSubmit={addUpdateCompany} enableReinitialize validationSchema={formikValidationSchema}>
      {({ values, setFieldValue, isValid, errors, touched, setTouched, validateForm, submitCount, submitForm }) => {
        const isValueEmpty = value => isNil(value) || value === ''
        const touchStepOneFields = () => {
          const touchedUpdates = STEP_ONE_FIELDS.reduce((acc, field) => {
            acc[field] = true
            return acc
          }, {})
          setTouched({ ...touched, ...touchedUpdates })
        }

        const touchStepFourFields = fields => {
          const fieldsToTouch = fields && fields.length ? fields : STEP_FOUR_FIELDS
          const touchedUpdates = fieldsToTouch.reduce((acc, field) => {
            acc[field] = true
            return acc
          }, {})
          setTouched({ ...touched, ...touchedUpdates })
        }

        const touchPhoneFields = () => {
          const touchedPhones = (values?.phones || []).map(() => ({
            title: true,
            phone: true,
          }))
          setTouched({ ...touched, phones: touchedPhones })
        }

        const handleEditUpdateClick = async () => {
          const formErrors = await validateForm()
          if (size(formErrors) > 0) {
            setTouched(setNestedObjectValues(formErrors, true), false)
            notify.error('Validation fail', null, flattenFormikErrors(formErrors))
            return
          }
          submitForm()
        }

        const handleNextClick = async event => {
          event?.preventDefault()
          if (activeStep === 1 || activeStep === 3 || activeStep === 4) {
            const currentErrors = await validateForm()

            if (activeStep === 1) {
              touchStepOneFields()
              const hasMissingRequired = some(STEP_ONE_FIELDS, field => isValueEmpty(get(values, field)))
              const hasSchemaErrors = some(STEP_ONE_FIELDS, field => get(currentErrors, field))
              if (hasMissingRequired || hasSchemaErrors) {
                return
              }
            }

            if (activeStep === 3) {
              touchPhoneFields()
              const hasPhoneValueGaps = some(values?.phones, phone => isValueEmpty(get(phone, 'title')) || isValueEmpty(get(phone, 'phone')))
              const phoneErrors = get(currentErrors, 'phones')
              const hasPhoneErrors = isArray(phoneErrors) ? some(phoneErrors, errorEntry => errorEntry && (errorEntry.title || errorEntry.phone)) : Boolean(phoneErrors)

              if (hasPhoneValueGaps || hasPhoneErrors) {
                return
              }
            }

            if (activeStep === 4) {
              const fieldsToValidate = [...STEP_FOUR_FIELDS]
              // if (values.nationalDayOffer) {
              //   fieldsToValidate.push('nationalDayDiscount')
              // }
              if (toBoolean(values.deliveryFeeWaiverEnabled)) {
                fieldsToValidate.push('deliveryFeeWaiverMinDays')
              }
              if (toBoolean(values.minRentalDaysEnabled)) {
                fieldsToValidate.push('minRentalDays')
              }

              touchStepFourFields(fieldsToValidate)
              const hasMissingRequired = some(fieldsToValidate, field => isValueEmpty(get(values, field)))
              const hasSchemaErrors = some(fieldsToValidate, field => get(currentErrors, field))
              if (hasMissingRequired || hasSchemaErrors) {
                return
              }
            }
          }

          handleNext()
        }

        return !loading ? (
          <div className="mb-[60px] flex w-full min-w-0 flex-col overflow-visible">
            <div className="flex w-full min-w-0 overflow-visible bg-white">
              <Form className="mx-auto w-full space-y-8 overflow-visible px-8 py-8">
                <div>
                  <div className="border-b border-gray-200 pb-5 sm:pb-0">
                    <h3 className="text-xl font-medium leading-6 text-gray-900">
                      {t('companyProfile.title')} {readOnly ? t('companyProfile.readOnly') : ''}
                    </h3>
                    {isEdit && isDnsProvisioningLocked && (
                      <div className="mt-4 flex flex-col gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-sm text-amber-900">{t('companyProfile.dnsProvisioningLockedBanner')}</p>
                        <button
                          type="button"
                          onClick={handleDnsStatusRefresh}
                          disabled={dnsBannerRefreshing || !refetch}
                          className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md border border-amber-300 bg-white px-3 py-1.5 text-sm font-medium text-amber-900 shadow-sm hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          <RefreshIcon className={`h-4 w-4 ${dnsBannerRefreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
                          {dnsBannerRefreshing ? t('companyProfile.dnsRefreshing') : t('companyProfile.dnsRefreshStatus')}
                        </button>
                      </div>
                    )}
                    <div className="mt-3 sm:mt-4">
                      {!isEdit ? (
                        /* Stepper for add mode */
                        <div className="mb-6">
                          <Stepper steps={getSteps()} activeStep={activeStep} />
                        </div>
                      ) : (
                        /* Tabs for edit mode — match UserProfile.jsx */
                        <div className="block">
                          <nav className="-mb-px flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-8">
                            {tabs.map(tab => (
                              <span
                                key={tab.name}
                                onClick={() => setCurrentTab(tab.id)}
                                className={classNames(
                                  tab.id === params?.t
                                    ? 'border-l-indigo-500 sm:border-l-transparent sm:border-b-indigo-500 text-indigo-600'
                                    : 'border-l-transparent sm:border-b-transparent text-gray-500 hover:text-gray-700 hover:border-l-gray-300 sm:hover:border-b-gray-300',
                                  'whitespace-nowrap py-2 px-3 sm:py-4 sm:px-0 border-l-4 sm:border-l-0 sm:border-b-2 font-medium text-sm cursor-pointer rounded-r-md sm:rounded-none'
                                )}
                                aria-current={params?.t === tab.id ? 'page' : undefined}
                              >
                                {tab.name}
                              </span>
                            ))}
                          </nav>
                        </div>
                      )}
                    </div>
                  </div>
                  {((!isEdit && activeStep === 1) || (isEdit && params?.t === 1)) && (
                    <div className="space-y-6 sm:space-y-5 pt-8">
                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-center sm:pt-5">
                        <label htmlFor="photo" className="block text-sm font-medium text-gray-700">
                          <LabelContent label={t('companyProfile.form.companyLogo')} required={!isEdit} />
                        </label>
                        <div className="mt-1 sm:mt-0 sm:col-span-2">
                          <div className="flex pb-4 items-center">
                            <div className="bg-gray relative h-[200px] w-[150px] border mr-4 border-dashed border-gray-300 rounded-md">
                              <input
                                id="logoUrl"
                                ref={profileRef}
                                name="logoUrl"
                                className="hidden"
                                accept=".jpg,.jpeg,.png"
                                type="file"
                                onChange={event => {
                                  showPreview('logoUrl', event.target.files[0])
                                  setFiles({ ...files, logoUrl: event.target.files[0] })
                                  setFieldValue('logoUrl', event.target.files[0] ? event.target.files[0].name : '')
                                }}
                              />
                              {previews.logoUrl ? (
                                <span onClick={() => profileRef.current.click()}>
                                  <img src={previews.logoUrl} alt="" className="cursor-pointer bg-cover  h-full w-[150px] rounded" />
                                </span>
                              ) : (
                                <div
                                  onClick={() => profileRef.current.click()}
                                  className="bg-white flex p-2 items-center justify-center bg-contain bg-no-repeat bg-center border-gray-300 rounded-md h-full w-full cursor-pointer"
                                >
                                  <CameraIcon className="text-gray-400 h-8 w-8" />
                                </div>
                              )}
                            </div>
                          </div>
                          {touched?.logoUrl && errors?.logoUrl && <p className="mt-2 text-sm text-red-600">{errors.logoUrl}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="name" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('companyProfile.form.companyName')} required={isFieldRequired('name')} />
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="text"
                            name="name"
                            as={Input}
                            placeholder={t('companyProfile.placeholders.companyName')}
                            extraClassName="w-full"
                            id="name"
                            autoComplete="given-name"
                            className="max-w-lg block w-full shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="branch" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('companyProfile.form.branches')} required={isEdit && isFieldRequired('branch')} />
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Selection
                            onChange={branchIds => setFieldValue('branch', branchIds || [])}
                            title={t('companyProfile.placeholders.selectBranch')}
                            multiple={true}
                            list={branchNames}
                            value={values?.branch}
                            className="w-full"
                            disabled={isFetchingBranches}
                          />
                        </div>
                        {isEdit && submitCount > 0 && errors?.branch && (
                          <div className="sm:col-span-2 sm:col-start-2 mt-2">
                            <p className="text-sm text-red-600">{errors.branch}</p>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="country" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('companyProfile.form.country')} required={isFieldRequired('country')} />
                        </label>
                        <div className="mt-1 sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Selection
                            onChange={value => {
                              const nextCountry = value?.id || ''
                              setFieldValue('country', nextCountry)
                              setFieldValue('governorate', '')
                              fetchMasterCities(nextCountry)
                            }}
                            title={t('companyProfile.placeholders.selectCountry')}
                            list={getCountryOptions()}
                            value={values?.country || ''}
                            className="w-full"
                            disabled={readOnly}
                          />
                          {touched?.country && errors?.country && <p className="mt-2 text-sm text-red-600">{errors.country}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="governorate" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('companyProfile.form.governorate')} required={isFieldRequired('governorate')} />
                        </label>
                        <div className="mt-1 sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Selection
                            onChange={value => {
                              if (value?.id) {
                                setFieldValue('governorate', value.id)
                              }
                            }}
                            title={t('companyProfile.placeholders.selectGovernorate')}
                            list={getGovernorateOptions(values?.governorate)}
                            value={values?.governorate || ''}
                            className="w-full"
                            disabled={readOnly || !values?.country}
                          />
                          {touched?.governorate && errors?.governorate && <p className="mt-2 text-sm text-red-600">{errors.governorate}</p>}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="email" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('companyProfile.form.email')} required={isFieldRequired('email')} />
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="text"
                            name="email"
                            as={Input}
                            placeholder={t('companyProfile.placeholders.enterEmail')}
                            extraClassName="w-full"
                            id="email"
                            className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="address" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('companyProfile.form.address')} required={isFieldRequired('address')} />
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="text"
                            name="address"
                            as={Input}
                            placeholder={t('companyProfile.placeholders.enterCompanyAddress')}
                            extraClassName="w-full"
                            id="address"
                            className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="minAge" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('companyProfile.form.minAge')} required={isFieldRequired('minAge')} />
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="text"
                            name="minAge"
                            extraClassName="w-full"
                            as={Input}
                            id="minAge"
                            className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="maxAge" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          <LabelContent label={t('companyProfile.form.maxAge')} required={isFieldRequired('maxAge')} />
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="text"
                            name="maxAge"
                            as={Input}
                            extraClassName="w-full"
                            id="maxAge"
                            className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="blockedCountries" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          {t('companyProfile.form.blockedCountries')}
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Selection
                            onChange={blockedCountryIds => setFieldValue('blockedCountries', blockedCountryIds || [])}
                            title={t('companyProfile.placeholders.selectCountries')}
                            multiple={true}
                            showSearch
                            list={localizedCountries}
                            value={values?.blockedCountries}
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {((!isEdit && activeStep === 2) || (isEdit && params?.t === 2)) && (
                    <>
                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="officeInstructionUserContract" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          {t('companyProfile.form.userContracts')}
                        </label>
                        <div className="mt-1 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:mt-0 sm:col-span-2">
                          <input
                            type="file"
                            name="officeInstructionUserContract"
                            accept=".jpg,.jpeg,.png,.webp,.pdf,.document,.docx,.xls,.xlsx,.plain"
                            className="w-full sm:w-64"
                            onChange={event => {
                              const file = event.target.files[0]
                              setFiles({ ...files, officeInstructionUserContract: file })
                              // eslint-disable-next-line
                              if (file && file.type.startsWith('image/')) {
                                showPreview('officeInstructionUserContract', file)
                              }
                            }}
                          />
                          {companyData?.officeInstructionUserContract && (
                            <div className="flex items-center">
                              <a
                                href={companyData.officeInstructionUserContract}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                              >
                                <DownloadIcon className="h-5 w-5 mr-3" aria-hidden="true" />
                                {t('companyProfile.form.downloadContract')}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="space-y-6 sm:space-y-5 pt-8">
                        <FieldArray name="officeInstructions">
                          {({ push, remove }) => (
                            <div>
                              {size(values.officeInstructions) > 0 &&
                                values.officeInstructions.map((instruction, index) => (
                                  <div key={index}>
                                    <div className="grid grid-cols-1 gap-4 mb-6">
                                      <div>
                                        <label htmlFor={`officeInstructions.${index}`} className="block mb-3 text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                                          {t('companyProfile.form.officeInstructionTitleEnglish')} {index + 1}
                                        </label>
                                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                                          <Field
                                            type="text"
                                            name={`officeInstructions.${index}.title.en`}
                                            as={Input}
                                            extraClassName="w-full"
                                            id={`officeInstructions.${index}`}
                                            className="block shadow-sm w-full focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                                          />
                                        </div>
                                      </div>
                                      <div>
                                        <label htmlFor={`officeInstructions.${index}`} className="block text-sm text-normal mb-3 text-gray-700 sm:mt-px sm:pt-2">
                                          {t('companyProfile.form.officeInstructionTitleArabic')} {index + 1}
                                        </label>
                                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg gap-3">
                                          <Field
                                            type="text"
                                            name={`officeInstructions.${index}.title.ar`}
                                            as={Input}
                                            extraClassName="w-full"
                                            id={`officeInstructions.${index}`}
                                            className="block shadow-sm w-full focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4">
                                      <div>
                                        <label htmlFor={`officeInstructions.description.${index}`} className="block mb-3 text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                                          {t('companyProfile.form.officeInstructionDescriptionEnglish')} {index + 1}
                                        </label>
                                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                                          <Field
                                            type="text"
                                            name={`officeInstructions.${index}.description.en`}
                                            as={TextArea}
                                            rows="5"
                                            extraClassName="w-full"
                                            id={`officeInstructions.${index}`}
                                            className="block shadow-sm w-full focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                                          />
                                        </div>
                                      </div>
                                      <div>
                                        <label htmlFor={`officeInstructions.description.${index}`} className="block text-sm text-normal mb-3 text-gray-700 sm:mt-px sm:pt-2">
                                          {t('companyProfile.form.officeInstructionDescriptionArabic')} {index + 1}
                                        </label>
                                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg gap-3">
                                          <div className="flex w-full gap-2">
                                            <Field
                                              type="text"
                                              name={`officeInstructions.${index}.description.ar`}
                                              as={TextArea}
                                              rows="5"
                                              extraClassName="w-full"
                                              id={`officeInstructions.${index}`}
                                              className="block shadow-sm w-full focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                                            />
                                          </div>
                                          <div className="flex gap-2">
                                            <button type="button" onClick={() => remove(index)} className="text-red-600 hover:text-red-700">
                                              <TrashIcon className="w-6 h-6" />
                                            </button>

                                            {index + 1 === size(values?.officeInstructions) && (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  push({
                                                    title: { ar: '', en: '' },
                                                    description: { ar: '', en: '' },
                                                  })
                                                }
                                                className="text-dark-600"
                                              >
                                                <PlusCircleIcon className="w-6 h-6" />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}

                              {size(values?.officeInstructions) === 0 && (
                                <button
                                  className="inline-flex items-center justify-end p-2 border border-transparent rounded-md shadow-sm text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                                  type="button"
                                  onClick={() =>
                                    push({
                                      title: { ar: '', en: '' },
                                      description: { ar: '', en: '' },
                                    })
                                  }
                                >
                                  {t('companyProfile.buttons.addOfficeInstruction')}
                                </button>
                              )}
                            </div>
                          )}
                        </FieldArray>
                      </div>
                    </>
                  )}

                  {((!isEdit && activeStep === 3) || (isEdit && params?.t === 3)) && (
                    <div className="space-y-6 sm:space-y-5 pt-8">
                      <FieldArray name="phones">
                        {({ push, remove }) => (
                          <div>
                            {size(values.phones) > 0 &&
                              values.phones.map((phone, index) => (
                                <div key={index} className="grid grid-cols-1 gap-4 mb-6">
                                  <div>
                                    <label htmlFor={`phones.${index}.title`} className="block mb-3 text-sm text-normal text-gray-700">
                                      <LabelContent label={`${t('companyProfile.form.phoneTitle')} ${index + 1}`} required={isFieldRequired('phones.title')} />
                                    </label>
                                    <div className="mt-1 flex w-full max-w-lg">
                                      <Field
                                        type="text"
                                        name={`phones.${index}.title`}
                                        as={Input}
                                        extraClassName="w-full"
                                        id={`phones.${index}.title`}
                                        className="block shadow-sm w-full focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label htmlFor={`phones.${index}.phone`} className="block mb-3 text-sm text-normal text-gray-700">
                                      <LabelContent label={`${t('companyProfile.form.phoneNumber')} ${index + 1}`} required={isFieldRequired('phones.phone')} />
                                    </label>
                                    <div className="mt-1 flex w-full max-w-lg gap-2">
                                      <Field
                                        type="text"
                                        name={`phones.${index}.phone`}
                                        as={Input}
                                        extraClassName="w-full"
                                        id={`phones.${index}.phone`}
                                        className="block shadow-sm w-full focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                                      />
                                      <div className="flex gap-2">
                                        <button type="button" onClick={() => remove(index)} className="text-red-600 hover:text-red-700">
                                          <TrashIcon className="w-6 h-6" />
                                        </button>

                                        {index + 1 === size(values?.phones) && (
                                          <button type="button" onClick={() => push({ title: '', phone: '' })} className="text-dark-600">
                                            <PlusCircleIcon className="w-6 h-6" />
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}

                            {size(values?.phones) === 0 && (
                              <button
                                className="inline-flex items-center justify-end p-2 border border-transparent rounded-md shadow-sm text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                                type="button"
                                onClick={() => push({ title: '', phone: '' })}
                              >
                                {t('companyProfile.buttons.addPhoneNumber')}
                              </button>
                            )}
                          </div>
                        )}
                      </FieldArray>
                    </div>
                  )}

                  {((!isEdit && activeStep === 4) || (isEdit && params?.t === 4)) && (
                    <div className="grid grid-cols-1 gap-5 pt-8 pb-10 lg:grid-cols-2">
                      <ConfigSection title={t('companyProfile.configSections.general.title')} description={t('companyProfile.configSections.general.description')}>
                        <ConfigToggleRow
                          id="isActive"
                          label={t('companyProfile.form.isActive')}
                          value={values.isActive}
                          onChange={checked => setFieldValue('isActive', checked, false)}
                        />
                        <ConfigToggleRow
                          id="hideOnTenant"
                          label={t('companyProfile.form.hideOnTenant')}
                          value={values.hideOnTenant}
                          onChange={checked => setFieldValue('hideOnTenant', checked, false)}
                        />
                        <ConfigToggleRow
                          id="hasFraudUserAccess"
                          label={t('companyProfile.form.fraudUserAccess')}
                          value={values.hasFraudUserAccess}
                          onChange={checked => setFieldValue('hasFraudUserAccess', checked, false)}
                        />
                        <ConfigToggleRow
                          id="showOfficeLogo"
                          label={t('companyProfile.form.showOfficeLogo')}
                          hint={t('companyProfile.form.showOfficeLogoHint')}
                          value={values.showOfficeLogo}
                          onChange={checked => setFieldValue('showOfficeLogo', checked, false)}
                        />
                      </ConfigSection>

                      <ConfigSection title={t('companyProfile.configSections.booking.title')} description={t('companyProfile.configSections.booking.description')}>
                        <ConfigToggleRow
                          id="instantBooking"
                          label={t('companyProfile.form.advanceBooking')}
                          value={values.instantBooking}
                          onChange={checked => setFieldValue('instantBooking', checked, false)}
                        />
                        <ConfigToggleRow
                          id="requiresCardHandover"
                          label={t('companyProfile.form.requiresCardHandover')}
                          hint={t('companyProfile.form.requiresCardHandoverHint')}
                          value={values.requiresCardHandover}
                          onChange={checked => setFieldValue('requiresCardHandover', checked, false)}
                        />
                        <ConfigToggleRow
                          id="minRentalDaysEnabled"
                          label={t('companyProfile.form.minRentalDaysEnabled')}
                          value={toBoolean(values.minRentalDaysEnabled)}
                          onChange={checked => {
                            // Keep the entered value while the toggle is off so switching
                            // it back on restores it. It is only validated and submitted
                            // while enabled, so a hidden value is inert.
                            setFieldValue('minRentalDaysEnabled', checked)
                          }}
                        >
                          {toBoolean(values.minRentalDaysEnabled) ? (
                            <div>
                              <label htmlFor="minRentalDays" className="mb-1.5 block text-sm text-gray-700">
                                <LabelContent label={t('companyProfile.form.minRentalDays')} required />
                              </label>
                              <Field
                                type="number"
                                name="minRentalDays"
                                min="1"
                                extraClassName="w-full"
                                as={Input}
                                id="minRentalDays"
                                placeholder={t('companyProfile.placeholders.minRentalDays')}
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                              />
                            </div>
                          ) : null}
                        </ConfigToggleRow>
                      </ConfigSection>

                      <ConfigSection title={t('companyProfile.configSections.services.title')} description={t('companyProfile.configSections.services.description')}>
                        <ConfigToggleRow
                          id="isFreeReplacementAvailable"
                          label={t('companyProfile.form.freeReplacement')}
                          value={values.isFreeReplacementAvailable}
                          onChange={checked => setFieldValue('isFreeReplacementAvailable', checked, false)}
                        />
                        <ConfigToggleRow
                          id="isFreeBreakdownServiceAvailable"
                          label={t('companyProfile.form.freeBreakdownService')}
                          value={values.isFreeBreakdownServiceAvailable}
                          onChange={checked => setFieldValue('isFreeBreakdownServiceAvailable', checked, false)}
                        />
                        <ConfigToggleRow
                          id="isExtrasEnabled"
                          label={t('companyProfile.form.isExtrasEnabled')}
                          value={values.isExtrasEnabled}
                          onChange={checked => setFieldValue('isExtrasEnabled', checked, false)}
                        />
                      </ConfigSection>

                      <ConfigSection title={t('companyProfile.configSections.insurance.title')} description={t('companyProfile.configSections.insurance.description')}>
                        <ConfigToggleRow
                          id="includeStandardInsuranceInBookingPayment"
                          label={t('companyProfile.form.includeStandardInsuranceInBookingPayment')}
                          value={values.includeStandardInsuranceInBookingPayment}
                          onChange={checked => setFieldValue('includeStandardInsuranceInBookingPayment', checked, false)}
                        />
                        <ConfigToggleRow
                          id="includeFullInsuranceInBookingPayment"
                          label={t('companyProfile.form.includeFullInsuranceInBookingPayment')}
                          value={values.includeFullInsuranceInBookingPayment}
                          onChange={checked => setFieldValue('includeFullInsuranceInBookingPayment', checked, false)}
                        />
                      </ConfigSection>

                      <ConfigSection title={t('companyProfile.configSections.deliveryWaiver.title')} description={t('companyProfile.configSections.deliveryWaiver.description')}>
                        <ConfigToggleRow
                          id="deliveryFeeWaiverEnabled"
                          label={t('companyProfile.form.deliveryFeeWaiverEnabled')}
                          value={toBoolean(values.deliveryFeeWaiverEnabled)}
                          onChange={checked => {
                            setFieldValue('deliveryFeeWaiverEnabled', checked)
                            if (!checked) {
                              setFieldValue('deliveryFeeWaiverMinDays', '')
                            }
                          }}
                        >
                          {toBoolean(values.deliveryFeeWaiverEnabled) ? (
                            <div>
                              <label htmlFor="deliveryFeeWaiverMinDays" className="mb-1.5 block text-sm text-gray-700">
                                <LabelContent label={t('companyProfile.form.deliveryFeeWaiverMinDays')} required />
                              </label>
                              <Field
                                type="number"
                                name="deliveryFeeWaiverMinDays"
                                extraClassName="w-full"
                                as={Input}
                                id="deliveryFeeWaiverMinDays"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                              />
                            </div>
                          ) : null}
                        </ConfigToggleRow>
                      </ConfigSection>

                      <ConfigSection title={t('companyProfile.configSections.fees.title')} description={t('companyProfile.configSections.fees.description')}>
                        <ConfigFieldRow htmlFor="ajarleePercentage" label={t('companyProfile.form.ajarleePercentage')}>
                          <Field
                            type="number"
                            name="ajarleePercentage"
                            extraClassName="w-full"
                            as={Input}
                            id="ajarleePercentage"
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                          />
                        </ConfigFieldRow>
                        <ConfigFieldRow htmlFor="vatTax" label={t('companyProfile.form.vatTax')} required={isFieldRequired('vatTax')}>
                          <Field
                            type="number"
                            name="vatTax"
                            extraClassName="w-full"
                            as={Input}
                            id="vatTax"
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                          />
                        </ConfigFieldRow>
                        <ConfigFieldRow
                          htmlFor="paymentGatewayPercentage"
                          label={t('companyProfile.form.paymentGatewayPercentage')}
                          required={isFieldRequired('paymentGatewayPercentage')}
                        >
                          <Field
                            type="number"
                            name="paymentGatewayPercentage"
                            extraClassName="w-full"
                            as={Input}
                            id="paymentGatewayPercentage"
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                          />
                        </ConfigFieldRow>
                      </ConfigSection>
                    </div>
                  )}
                  {isEdit && params?.t === 5 && (
                    <div className="grid grid-cols-1 gap-5 pt-8 pb-10 lg:grid-cols-2">
                      <ConfigSection title={t('companyProfile.configSections.deliveryFee.title')} description={t('companyProfile.configSections.deliveryFee.description')}>
                        <ConfigFieldRow htmlFor="deliveryFee" label={t('companyProfile.form.deliveryFee')}>
                          <Field
                            type="number"
                            name="deliveryFee"
                            extraClassName="w-full"
                            as={Input}
                            id="deliveryFee"
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                          />
                        </ConfigFieldRow>
                      </ConfigSection>

                      <ConfigSection title={t('companyProfile.configSections.freeDelivery.title')} description={t('companyProfile.configSections.freeDelivery.description')}>
                        <ConfigToggleRow
                          id="isFreeDeliveryAvailable"
                          label={t('companyProfile.form.enableFreeDelivery')}
                          value={values.isFreeDeliveryAvailable}
                          onChange={checked => setFieldValue('isFreeDeliveryAvailable', checked)}
                        >
                          {values.isFreeDeliveryAvailable ? (
                            <div className="space-y-4">
                              <div>
                                <label htmlFor="effectiveStartDate" className="mb-1.5 block text-sm text-gray-700">
                                  {t('companyProfile.form.effectiveStartDate')}
                                </label>
                                <Field
                                  type="datetime-local"
                                  name="effectiveStartDate"
                                  extraClassName="w-full"
                                  as={Input}
                                  id="effectiveStartDate"
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                                />
                              </div>
                              <div>
                                <label htmlFor="effectiveEndDate" className="mb-1.5 block text-sm text-gray-700">
                                  {t('companyProfile.form.effectiveEndDate')}
                                </label>
                                <Field
                                  type="datetime-local"
                                  name="effectiveEndDate"
                                  extraClassName="w-full"
                                  as={Input}
                                  id="effectiveEndDate"
                                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-dark-500 focus:ring-dark-500 sm:text-sm"
                                />
                              </div>
                              <div>
                                <label htmlFor="freeDeliveryFor" className="mb-1.5 block text-sm text-gray-700">
                                  {t('companyProfile.form.freeDeliveryFor')}
                                </label>
                                <Selection
                                  onChange={selectedValue => {
                                    setFieldValue('freeDeliveryFor', selectedValue?.id || '')
                                  }}
                                  title={t('companyProfile.form.selectFreeDeliveryFor')}
                                  list={[
                                    { id: 'user', name: t('companyProfile.form.freeDeliveryAudienceUser') },
                                    { id: 'campaign', name: t('companyProfile.form.freeDeliveryAudienceCampaign') },
                                    { id: 'all', name: t('companyProfile.form.freeDeliveryAudienceAll') },
                                  ]}
                                  value={values?.freeDeliveryFor}
                                  className="w-full"
                                />
                              </div>
                              {values?.freeDeliveryFor === 'user' && (
                                <div>
                                  <label htmlFor="freeDeliveryUsers" className="mb-1.5 block text-sm text-gray-700">
                                    {t('companyProfile.form.selectUsers')}
                                  </label>
                                  <Selection
                                    onChange={userIds => setFieldValue('freeDeliveryUsers', userIds || [])}
                                    title={t('companyProfile.form.selectUsers')}
                                    multiple={true}
                                    showSearch
                                    onSearch={searchQuery => fetchUsers(searchQuery)}
                                    list={userOptions}
                                    value={values?.freeDeliveryUsers}
                                    className="w-full"
                                  />
                                </div>
                              )}
                              {values?.freeDeliveryFor === 'campaign' && (
                                <div>
                                  <label htmlFor="freeDeliveryCampaignUsers" className="mb-1.5 block text-sm text-gray-700">
                                    {t('companyProfile.form.selectCampaigns')}
                                  </label>
                                  <Selection
                                    onChange={campaignIds => setFieldValue('freeDeliveryCampaignUsers', campaignIds || [])}
                                    title={t('companyProfile.form.selectCampaigns')}
                                    multiple={true}
                                    list={campaignOptions}
                                    value={values?.freeDeliveryCampaignUsers}
                                    className="w-full"
                                  />
                                </div>
                              )}
                            </div>
                          ) : null}
                        </ConfigToggleRow>
                      </ConfigSection>
                    </div>
                  )}

                  {isEdit && params?.t === 6 && (
                    <div className="space-y-6 sm:space-y-5 pt-8 pb-10">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center">
                        <p className="whitespace-nowrap">Featured</p>
                        <Switch
                          id="featured"
                          value={values.featured}
                          onChange={checked => {
                            setFieldValue('featured', checked)
                          }}
                        />
                      </div>

                      {values.featured && (
                        <>
                          <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:pt-5 mb-6">
                            <label htmlFor="featuredEffectiveStartDate" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                              Featured Effective Start Date
                            </label>
                            <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                              <Field
                                type="datetime-local"
                                name="featuredEffectiveStartDate"
                                extraClassName="w-full"
                                as={Input}
                                id="featuredEffectiveStartDate"
                                className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:pt-5 mb-6">
                            <label htmlFor="featuredEffectiveEndDate" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                              Featured Effective End Date
                            </label>
                            <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                              <Field
                                type="datetime-local"
                                name="featuredEffectiveEndDate"
                                extraClassName="w-full"
                                as={Input}
                                id="featuredEffectiveEndDate"
                                className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:pt-5 mb-6">
                            <label htmlFor="featuredCarsPerRow" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                              Cars per row
                            </label>
                            <div className="mt-1 flex flex-col sm:mt-0 w-full max-w-lg sm:col-span-2">
                              <Field
                                type="number"
                                min={0}
                                max={companyCars.length}
                                name="featuredCarsPerRow"
                                extraClassName="w-full"
                                as={Input}
                                id="featuredCarsPerRow"
                                className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                              />
                              {errors?.featuredCarsPerRow && touched?.featuredCarsPerRow ? (
                                <span className="text-xs text-red-500 mt-1">{errors.featuredCarsPerRow}</span>
                              ) : (
                                <span className="text-xs text-gray-500 mt-1">
                                  Cars per row can be at most <span className="font-semibold text-gray-700">{companyCars.length}</span> (this company has {companyCars.length}{' '}
                                  {companyCars.length === 1 ? 'car' : 'cars'}).
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="border-t border-gray-200 sm:pt-5">
                            <div className="flex items-start justify-between gap-3 mb-3">
                              <div>
                                <p className="text-sm font-medium text-gray-900">Featured Cars</p>
                                <p className="text-xs text-gray-500 mt-0.5">Select which cars to feature and drag to set the order they appear in.</p>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setFeaturedCarsBackup(values.featuredCars || [])
                                  setFeaturedCarsPreSelectAll(null)
                                  setShowCarsModal(true)
                                }}
                                disabled={isFetchingCompanyCars || companyCars.length === 0}
                                className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-dark-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-dark-700 disabled:opacity-50"
                              >
                                <PlusCircleIcon className="h-4 w-4" />
                                Select cars
                              </button>
                            </div>

                            {isFetchingCompanyCars ? (
                              <div className="py-6 flex justify-center">
                                <LoadingIcon />
                              </div>
                            ) : companyCars.length === 0 ? (
                              <p className="text-sm text-gray-500 py-4">This company has no cars.</p>
                            ) : (
                              <div>
                                <p className="text-xs font-semibold text-gray-600 uppercase mb-2">Selected order ({(values.featuredCars || []).length})</p>
                                {(values.featuredCars || []).length === 0 ? (
                                  <p className="text-sm text-gray-400 border border-dashed border-gray-200 rounded-lg px-3 py-6 text-center">
                                    No cars selected yet. Click “Select cars” to feature some.
                                  </p>
                                ) : (
                                  <DndProvider backend={HTML5Backend}>
                                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                                      {(values.featuredCars || []).map((carId, index) => {
                                        const car = find(companyCars, c => String(c._id) === String(carId)) || { _id: carId }
                                        return (
                                          <DraggableFeaturedCarCard
                                            key={String(carId)}
                                            car={car}
                                            index={index}
                                            isArabic={isArabic}
                                            moveCar={(dragIndex, hoverIndex) => {
                                              const updated = [...(values.featuredCars || [])]
                                              const [removed] = updated.splice(dragIndex, 1)
                                              updated.splice(hoverIndex, 0, removed)
                                              setFieldValue('featuredCars', updated)
                                            }}
                                            onRemove={() => {
                                              setFieldValue(
                                                'featuredCars',
                                                (values.featuredCars || []).filter(cid => String(cid) !== String(carId))
                                              )
                                            }}
                                          />
                                        )
                                      })}
                                    </div>
                                  </DndProvider>
                                )}
                              </div>
                            )}

                            <Modal
                              open={showCarsModal}
                              onClose={() => {
                                setFeaturedCarsPreSelectAll(null)
                                setShowCarsModal(false)
                              }}
                              closeable
                              title="Select featured cars"
                              description={`${(values.featuredCars || []).length} selected`}
                              dimensions={{ width: 'min(960px, 92vw)', height: 'auto', maxHeight: '72vh' }}
                              padding="p-0"
                            >
                              <div className="flex max-h-[72vh] flex-col">
                                <div className="flex-1 overflow-y-auto p-6">
                                  {companyCars.length === 0 ? (
                                    <p className="text-sm text-gray-500 py-4">This company has no cars.</p>
                                  ) : (
                                    <>
                                      <label className="mb-4 flex cursor-pointer items-center gap-2.5 text-sm font-medium text-gray-700">
                                        <input
                                          type="checkbox"
                                          className="h-4 w-4 rounded border-gray-300 text-dark-600 focus:ring-dark-500"
                                          checked={every(companyCars, car => some(values.featuredCars || [], cid => String(cid) === String(car._id)))}
                                          onChange={() => {
                                            const allSelected = every(companyCars, car => some(values.featuredCars || [], cid => String(cid) === String(car._id)))
                                            if (allSelected) {
                                              setFieldValue('featuredCars', featuredCarsPreSelectAll ?? [])
                                              setFeaturedCarsPreSelectAll(null)
                                            } else {
                                              setFeaturedCarsPreSelectAll(values.featuredCars || [])
                                              setFieldValue(
                                                'featuredCars',
                                                companyCars.map(car => String(car._id))
                                              )
                                            }
                                          }}
                                        />
                                        Select All Cars
                                      </label>
                                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                                        {companyCars.map(car => {
                                          const selectedIndex = findIndex(values.featuredCars || [], cid => String(cid) === String(car._id))
                                          const isSelected = selectedIndex !== -1
                                          return (
                                            <SelectableFeaturedCarCard
                                              key={String(car._id)}
                                              car={car}
                                              isArabic={isArabic}
                                              isSelected={isSelected}
                                              order={selectedIndex + 1}
                                              onToggle={() => {
                                                const current = values.featuredCars || []
                                                if (isSelected) {
                                                  setFieldValue(
                                                    'featuredCars',
                                                    current.filter(cid => String(cid) !== String(car._id))
                                                  )
                                                } else {
                                                  setFieldValue('featuredCars', [...current, String(car._id)])
                                                }
                                              }}
                                            />
                                          )
                                        })}
                                      </div>
                                    </>
                                  )}
                                </div>

                                <div className="flex shrink-0 justify-end gap-3 border-t border-gray-200 bg-white px-6 py-3">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFieldValue('featuredCars', featuredCarsBackup)
                                      setFeaturedCarsPreSelectAll(null)
                                      setShowCarsModal(false)
                                    }}
                                    className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setFeaturedCarsPreSelectAll(null)
                                      setShowCarsModal(false)
                                    }}
                                    className="rounded-md bg-dark-600 px-4 py-2 text-sm font-semibold text-white hover:bg-dark-700"
                                  >
                                    Done
                                  </button>
                                </div>
                              </div>
                            </Modal>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {((!isEdit && activeStep === 5) || (isEdit && params?.t === 7)) && (
                    <div className="space-y-6 sm:space-y-5 pt-8 pb-10">
                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="brandName" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          {t('companyProfile.form.brandName')}
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="text"
                            name="brandName"
                            extraClassName="w-full"
                            as={Input}
                            id="brandName"
                            className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="certificateNumber" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          {t('companyProfile.form.certificateNumber')}
                        </label>
                        <div className="mt-1 flex sm:mt-0 w-full max-w-lg sm:col-span-2">
                          <Field
                            type="number"
                            name="certificateNumber"
                            extraClassName="w-full"
                            as={Input}
                            id="certificateNumber"
                            className="block shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start border-t border-gray-200 sm:pt-5 mb-6">
                        <label htmlFor="uploadCertificate" className="block text-sm text-normal text-gray-700 sm:mt-px sm:pt-2">
                          {t('companyProfile.form.uploadCertificate')}
                        </label>
                        <div className="mt-1 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:mt-0 sm:col-span-2">
                          <input
                            type="file"
                            name="uploadCertificate"
                            accept=".jpg,.jpeg,.png,.webp,.pdf,.document,.docx,.xls,.xlsx,.plain"
                            className="w-full sm:w-64"
                            onChange={event => {
                              const file = event.target.files[0]
                              setFiles({ ...files, uploadCertificate: file })
                              // eslint-disable-next-line
                              if (file && file.type.startsWith('image/')) {
                                showPreview('uploadCertificate', file)
                              }
                            }}
                          />
                          {companyData?.uploadCertificate && (
                            <div className="flex items-center">
                              <a
                                href={companyData.uploadCertificate}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                              >
                                <DownloadIcon className="h-5 w-5 mr-3" aria-hidden="true" />
                                {t('companyProfile.form.downloadCertificate')}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {isEdit && params?.t === 8 && canEcommerce && (
                    <CompanyEcommerceTab
                      companyId={id}
                      companyData={companyData}
                      refetch={refetch}
                      dnsEditLocked={isDnsProvisioningLocked}
                      canBranding={canBranding}
                      canDns={canDns}
                    />
                  )}
                </div>

                {!readOnly && (
                  <div className="pt-2">
                    {!isEdit ? (
                      /* Stepper navigation buttons for add mode */
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        {activeStep === getSteps().length ? (
                          <div className="flex flex-col sm:flex-row w-full gap-4">
                            <div className="flex flex-1 justify-start">
                              {activeStep > 1 && (
                                <button
                                  type="button"
                                  onClick={handlePrevious}
                                  className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                                >
                                  {'Previous'}
                                </button>
                              )}
                            </div>
                            <div className="flex flex-1 justify-end">
                              <button
                                type="submit"
                                disabled={!isValid || isSubmitting || isFetchingBranches}
                                className={`inline-flex justify-center py-2 px-6 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 ${
                                  (!isValid || isSubmitting || isFetchingBranches) && 'opacity-50 cursor-not-allowed'
                                }`}
                              >
                                {isSubmitting ? (
                                  <span className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                      ></path>
                                    </svg>
                                    <span>{t('companyProfile.buttons.pleaseWait')}</span>
                                  </span>
                                ) : (
                                  <span>{t('companyProfile.buttons.addCompany')}</span>
                                )}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col sm:flex-row w-full gap-4">
                            <div className="flex flex-1 justify-start">
                              {activeStep > 1 && (
                                <button
                                  type="button"
                                  onClick={handlePrevious}
                                  className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                                >
                                  {'Previous'}
                                </button>
                              )}
                            </div>
                            <div className="flex flex-1 justify-end">
                              {activeStep < getSteps().length && (
                                <button
                                  type="button"
                                  onClick={handleNextClick}
                                  className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500"
                                >
                                  {'Next'}
                                </button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Original buttons for edit mode */
                      <div className="flex flex-col sm:flex-row justify-between gap-4">
                        <div className="flex flex-1 justify-start">
                          {params?.t !== 7 && params?.t !== 8 && (
                            <button
                              type="button"
                              onClick={deleteCompany}
                              disabled={isFetchingBranches || isDnsProvisioningLocked}
                              className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 ${
                                (isFetchingBranches || isDnsProvisioningLocked) && 'opacity-50 cursor-not-allowed'
                              }`}
                            >
                              {t('companyProfile.buttons.deleteCompany')}
                            </button>
                          )}
                        </div>
                        <div className="flex flex-1 justify-end">
                          {params?.t !== 8 && (
                            <button
                              type="button"
                              onClick={handleEditUpdateClick}
                              disabled={isSubmitting || isFetchingBranches || isDnsProvisioningLocked}
                              className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 ${
                                (isSubmitting || isFetchingBranches || isDnsProvisioningLocked) && 'opacity-50 cursor-not-allowed'
                              }`}
                            >
                              {isSubmitting ? (
                                <span className="flex items-start">
                                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path
                                      className="opacity-75"
                                      fill="currentColor"
                                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    ></path>
                                  </svg>
                                  <span>{t('companyProfile.buttons.pleaseWait')}</span>
                                </span>
                              ) : (
                                <span>{t('companyProfile.buttons.update')}</span>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </Form>
            </div>
          </div>
        ) : (
          <div className="w-full h-[500px] flex items-center justify-center">
            <LoadingIcon />
          </div>
        )
      }}
    </Formik>
  )
}

export default CompanyProfile
